import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import ListSubheader from '@mui/material/ListSubheader';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select, { SelectProps } from '@mui/material/Select';
import { forwardRef, ReactNode } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

export type TMultiselectItem<T = unknown> = {
  id: string | number;
  name: string;
  value: string | number | T;
};

type TProps = SelectProps & {
  name: string;
  native?: boolean;
  helperText?: React.ReactNode;
  children: ReactNode | ReactNode[];
};

export const RHFSelect = forwardRef<HTMLDivElement, TProps>((props, ref) => {
  const { name, label, helperText, children, native, ...others } = props;

  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        // `displayEmpty` makes the Select render its empty-value option (e.g.
        // "Unassigned") instead of a blank box, so the label has to stay shrunk
        // or it sits on top of that text.
        const isLabelShrunk = !!field.value || field.value === 0 || Boolean(others.displayEmpty);

        return (
          <FormControl fullWidth ref={ref}>
            <InputLabel shrink={isLabelShrunk} id={`label-${name}`}>
              {label}
            </InputLabel>
            <Select
              {...field}
              name={name}
              labelId={`label-${name}`}
              label={label}
              native={native}
              input={<OutlinedInput label={label} notched={isLabelShrunk} />}
              fullWidth
              {...others}
            >
              <ListSubheader>Select an Option</ListSubheader>
              {children}
            </Select>
            {(!!error || helperText) && (
              <FormHelperText error={!!error}>{error ? error?.message : helperText}</FormHelperText>
            )}
          </FormControl>
        );
      }}
    />
  );
});
